/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { decorate, computed, observable, action, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Icon } from 'semantic-ui-react';
import c from 'classnames';

import { allAvailableCategories, getConfigCategory } from '../setup/EnvTypeAdditionalInfo';
import {
  descToHtml,
  formatDailyRate,
  formatHourlyRate,
  getHourlyRate,
  parseConfigSpecs,
} from '../../../helpers/instance-config';
import { getEstimatedCostForRegion } from '../../../helpers/estimated-cost-util';

/**
 * The cost shown for a config: the hourly rate when it can be read as a number, otherwise whatever
 * legacy text the admin entered.
 *
 * @returns {{ rate: number|null, text: string }}
 */
export function getConfigCost(config, region) {
  const rate = getHourlyRate(config.estimatedCostInfo, region);
  if (rate !== null) return { rate, text: formatHourlyRate(rate) };
  return { rate: null, text: getEstimatedCostForRegion(config.estimatedCostInfo, region) || '--' };
}

/**
 * Tabbed, compact list of instance configurations, shared by the create workspace page and the
 * change instance type modal. It is controlled: the parent owns the selection.
 *
 * Props:
 *  - configs: [{ id, name, instanceType, desc, estimatedCostInfo, isCurrent }]. `instanceType` may
 *    carry the `/N` core count suffix; `desc` is the admin-authored markdown.
 *  - selectedId: id of the selected config
 *  - onSelect(config): called when the user picks a config
 *  - groupName: name for the radio group, unique on the page
 *  - initialConfigId (optional): the tab holding this config is shown first. Defaults to selectedId.
 *  - region (optional): region used to resolve the estimated cost
 *  - costHeader (optional): heading for the cost column
 *  - disabled (optional): stops any selection
 *  - scrollable (optional): the table scrolls inside its own area rather than growing the page
 */
class InstanceConfigPicker extends React.Component {
  constructor(props) {
    super(props);
    this.tabRefs = {};
    runInAction(() => {
      this.selectedCategory = this.categoryOf(props.initialConfigId || props.selectedId);
      // Per row expand state set by the user. A row with no entry is expanded only when selected.
      this.expandOverrides = observable.map();
    });
  }

  get configs() {
    return this.props.configs || [];
  }

  // [{ name, configs }] in the order categories first appear in the config list, which is the order
  // the admin listed the configs in.
  get categories() {
    const byName = new Map();
    this.configs.forEach(config => {
      const name = getConfigCategory(config.name);
      if (!byName.has(name)) byName.set(name, []);
      byName.get(name).push(config);
    });
    return Array.from(byName, ([name, configs]) => ({ name, configs }));
  }

  get effectiveCategory() {
    const names = this.categories.map(category => category.name);
    if (this.selectedCategory && names.includes(this.selectedCategory)) return this.selectedCategory;
    return names[0] || null;
  }

  get selectedConfigCategory() {
    return this.categoryOf(this.props.selectedId);
  }

  categoryOf(configId) {
    if (!configId) return null;
    const config = (this.props.configs || []).find(item => item.id === configId);
    return config ? getConfigCategory(config.name) : null;
  }

  switchCategory = name => {
    this.selectedCategory = name;
  };

  // Arrow keys move between tabs, as the WAI-ARIA tabs pattern expects.
  handleTabKeyDown = event => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const names = this.categories.map(category => category.name);
    const index = names.indexOf(this.effectiveCategory);
    const step = event.key === 'ArrowRight' ? 1 : -1;
    const next = names[(index + step + names.length) % names.length];
    this.switchCategory(next);
    const tab = this.tabRefs[next];
    if (tab && typeof tab.focus === 'function') tab.focus();
  };

  isExpanded(config) {
    if (this.expandOverrides.has(config.id)) return this.expandOverrides.get(config.id);
    return config.id === this.props.selectedId;
  }

  toggleExpanded = config => {
    this.expandOverrides.set(config.id, !this.isExpanded(config));
  };

  canSelect(config) {
    return !this.props.disabled && !config.isCurrent;
  }

  handleSelect = config => {
    if (!this.canSelect(config)) return;
    this.props.onSelect(config);
  };

  domId(suffix) {
    return `${this.props.groupName}-${suffix}`.replace(/[^A-Za-z0-9_-]/g, '-');
  }

  render() {
    const category = this.effectiveCategory;
    if (!category) return null;
    const categoryInfo = allAvailableCategories[category];

    return (
      <div className={c('icp', { 'icp-scrollable': this.props.scrollable })}>
        {this.renderTabs()}
        <div
          role="tabpanel"
          id={this.domId('panel')}
          aria-labelledby={this.domId(`tab-${category}`)}
          className="icp-panel"
        >
          {categoryInfo && categoryInfo.desc && <p className="icp-category-desc">{categoryInfo.desc}</p>}
          {this.renderTable()}
        </div>
      </div>
    );
  }

  renderTabs() {
    return (
      <div className="icp-tabs" role="tablist" aria-label="Configuration categories">
        {this.categories.map(({ name, configs }) => {
          const selected = name === this.effectiveCategory;
          const info = allAvailableCategories[name];
          const color = info ? info.color : 'black';
          const holdsSelection = name === this.selectedConfigCategory;
          return (
            <button
              key={name}
              ref={el => {
                this.tabRefs[name] = el;
              }}
              type="button"
              role="tab"
              id={this.domId(`tab-${name}`)}
              aria-selected={selected}
              aria-controls={this.domId('panel')}
              tabIndex={selected ? 0 : -1}
              className={c('icp-tab', `icp-color-${color}`, { selected })}
              onClick={() => this.switchCategory(name)}
              onKeyDown={this.handleTabKeyDown}
              data-testid={`icp-tab-${name}`}
            >
              <span className="icp-tab-dot" aria-hidden="true" />
              <span className="icp-tab-name">{name}</span>
              <span className="icp-tab-count">
                {configs.length}
                <span className="icp-visually-hidden"> configurations</span>
              </span>
              {holdsSelection && (
                <span className="icp-tab-selection" title="Your selection is in this category">
                  <span className="icp-visually-hidden">(contains your selection)</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  renderTable() {
    const configs = (this.categories.find(item => item.name === this.effectiveCategory) || {}).configs || [];
    return (
      <div className="icp-table-wrapper">
        <table className="icp-table">
          <thead>
            <tr>
              <th className="icp-col-radio">
                <span className="icp-visually-hidden">Select</span>
              </th>
              <th>Configuration</th>
              <th>Instance</th>
              <th className="icp-col-number">CPU</th>
              <th className="icp-col-number">Threads</th>
              <th>Memory</th>
              <th>Notes</th>
              <th className="icp-col-cost">{this.props.costHeader || 'Cost'}</th>
              <th className="icp-col-expand">
                <span className="icp-visually-hidden">Details</span>
              </th>
            </tr>
          </thead>
          <tbody>{configs.map(config => this.renderRow(config))}</tbody>
        </table>
      </div>
    );
  }

  renderRow(config) {
    const selected = config.id === this.props.selectedId;
    const selectable = this.canSelect(config);
    const specs = parseConfigSpecs(config.desc);
    const cost = getConfigCost(config, this.props.region);
    const expanded = this.isExpanded(config);
    const hasDetails = !!specs.details || cost.rate !== null;
    const inputId = this.domId(`option-${config.id}`);
    const detailId = this.domId(`detail-${config.id}`);
    const instanceType = String(config.instanceType || '').split('/')[0];

    const rows = [
      // The row is a convenience click target; the radio input is what keyboard and screen reader
      // users operate, so the row itself needs no key handler.
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
      <tr
        key={config.id}
        className={c('icp-row', {
          selected,
          expanded: hasDetails && expanded,
          current: config.isCurrent,
          disabled: !selectable && !config.isCurrent,
        })}
        onClick={() => this.handleSelect(config)}
        data-testid={`icp-row-${config.id}`}
      >
        <td className="icp-cell-radio">
          <input
            type="radio"
            className="icp-radio"
            id={inputId}
            name={this.props.groupName}
            value={config.id}
            checked={selected}
            disabled={!selectable}
            aria-labelledby={`${inputId}-label`}
            onChange={() => this.handleSelect(config)}
            onClick={event => event.stopPropagation()}
          />
        </td>
        <td className="icp-cell-name">
          <label id={`${inputId}-label`} htmlFor={inputId}>
            {config.name}
          </label>
        </td>
        <td>
          <code className="icp-instance">{instanceType}</code>
        </td>
        {specs.hasSpecs ? (
          <>
            <td className="icp-col-number">{specs.cpus}</td>
            <td className="icp-col-number">{specs.threads || '--'}</td>
            <td className="icp-cell-memory">{specs.memory}</td>
          </>
        ) : (
          <td colSpan={3} className="icp-cell-summary" title={specs.summary}>
            {specs.summary || '--'}
          </td>
        )}
        <td className="icp-cell-notes">
          {config.isCurrent && <span className="icp-badge icp-badge-current">Current</span>}
          {specs.dcvUnsupported && (
            <span
              className="icp-badge icp-badge-warning"
              title="DCV desktop streaming is not supported. Connect with RDP or SSH instead."
            >
              No DCV
            </span>
          )}
        </td>
        <td className="icp-col-cost" title={cost.rate !== null ? formatDailyRate(cost.rate) : undefined}>
          {cost.text}
        </td>
        <td className="icp-col-expand">
          {hasDetails && (
            <button
              type="button"
              className="icp-expand"
              aria-expanded={expanded}
              aria-controls={detailId}
              aria-label={`${expanded ? 'Hide' : 'Show'} details for ${config.name}`}
              onClick={event => {
                event.stopPropagation();
                this.toggleExpanded(config);
              }}
              data-testid={`icp-expand-${config.id}`}
            >
              <Icon name={expanded ? 'chevron up' : 'chevron down'} />
            </button>
          )}
        </td>
      </tr>,
    ];

    if (hasDetails && expanded) {
      rows.push(
        <tr key={`${config.id}-detail`} id={detailId} className={c('icp-detail-row', { selected })}>
          <td colSpan={9}>
            {specs.details && (
              // eslint-disable-next-line react/no-danger
              <div className="icp-detail" dangerouslySetInnerHTML={{ __html: descToHtml(specs.details) }} />
            )}
            {cost.rate !== null && (
              <div className="icp-detail-cost">Estimated {formatDailyRate(cost.rate)} if left running all day.</div>
            )}
          </td>
        </tr>,
      );
    }

    return rows;
  }
}

decorate(InstanceConfigPicker, {
  selectedCategory: observable,
  configs: computed,
  categories: computed,
  effectiveCategory: computed,
  selectedConfigCategory: computed,
  switchCategory: action,
  toggleExpanded: action,
});

export default observer(InstanceConfigPicker);
